import type { ScreeningOutcome, SupportedLanguage } from '@kiku/config/constants';
import { atsProviderEnum } from '@kiku/db/schema';
import type { InterviewMessageRole, QuestionCategory } from '@kiku/db/schema';
import { z } from 'zod';

export const ATS_PROVIDERS = atsProviderEnum.enumValues;
export type AtsProvider = (typeof ATS_PROVIDERS)[number];

export const atsJobSchema = z.object({
  id: z.string(),
  title: z.string(),
  location: z.string().nullable(),
  description: z.string().nullable(),
  recruiterId: z.string().nullable(),
  externalData: z.record(z.string(), z.unknown()).optional()
});
export type ATSJob = z.infer<typeof atsJobSchema>;

export const atsStageSchema = z.object({
  id: z.string(),
  name: z.string()
});
export type ATSStage = z.infer<typeof atsStageSchema>;

export const stageMappingSchema = z.object({
  initialStage: z.string(),
  passInterviewStage: z.string().default(''),
  failInterviewStage: z.string().default(''),
  recommendedStage: z.string().default('')
});
export type StageMapping = z.infer<typeof stageMappingSchema>;

export const atsConfigSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  orgId: z.string().uuid(),
  provider: z.enum(ATS_PROVIDERS),
  apiKey: z.string(),
  stageMapping: stageMappingSchema,
  createdAt: z.date(),
  updatedAt: z.date()
});
export type AtsConfig = z.infer<typeof atsConfigSchema>;

const fullStageMappingSchema = z.object({
  initialStage: z.string(),
  passInterviewStage: z.string(),
  failInterviewStage: z.string(),
  recommendedStage: z.string()
});

export const saveAtsConfigInputSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('teamtailor'),
    apiKey: z.string().min(1).optional(),
    stageMapping: fullStageMappingSchema
  }),
  z.object({
    provider: z.literal('recman'),
    apiKey: z.string().min(1).optional(),
    stageMapping: fullStageMappingSchema
  }),
  z.object({
    provider: z.literal('logicmelon'),
    apiKey: z.string().min(1).optional(),
    stageMapping: z.object({ initialStage: z.string() })
  })
]);
export type SaveAtsConfigInput = z.infer<typeof saveAtsConfigInputSchema>;

export const atsApplicantSchema = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  externalId: z.string(),
  externalApplicationId: z.string(),
  externalJobId: z.string(),
  externalSource: z.string(),
  photo: z.string(),
  externalStage: z.string(),
  externalCreatedAt: z.string(),
  externalData: z.record(z.string(), z.unknown()),
  status: z.string(),
  candidateProfileUrl: z.string().optional()
});
export type ATSApplicant = z.infer<typeof atsApplicantSchema>;

export type PostInterviewData = {
  candidateName: string;
  jobTitle: string;
  outcome: ScreeningOutcome;
  overallScore: number;
  aiSummary: string;
  questionScores: Array<{
    category: QuestionCategory;
    questionText: string;
    verdict: string | null;
    score: number | null;
    reasoning: string;
    competencyName: string | null;
  }>;
  transcript: Array<{
    role: InterviewMessageRole;
    content: string;
    copyPasteDetected?: boolean;
    questionCategory?: QuestionCategory;
  }>;
  applicantProfileUrl: string;
};

export interface ATS {
  fetchActiveJobs(): Promise<ATSJob[]>;
  fetchApplicationStages(jobId?: string): Promise<ATSStage[]>;
  verifyToken(): Promise<'valid' | 'invalid' | 'error'>;
  fetchApplicants(jobId: string, stage: string): Promise<ATSApplicant[]>;

  canMoveApplicantTo(): Promise<boolean>;
  moveApplicantTo(
    jobData: {
      atsJobId: string;
      atsUserId: string | null;
      atsExternalData: Record<string, unknown> | null;
    },
    candidateData: { atsCandidateId: string; atsApplicationId: string },
    fromStage: string,
    toStage: string
  ): Promise<void>;

  canPostInterview(): Promise<boolean>;
  postInterview(
    candidateData: { candidateId: string; jobApplicationId?: string },
    userId: string,
    startedAt: Date,
    interviewData: PostInterviewData,
    orgLanguage: SupportedLanguage
  ): Promise<void>;
}

export const resolvedJobStagesSchema = z.object({
  stages: z.array(atsStageSchema),
  defaults: z.object({
    initialStage: z.string().nullable(),
    passInterviewStage: z.string().nullable(),
    failInterviewStage: z.string().nullable(),
    recommendedStage: z.string().nullable()
  })
});
export type ResolvedJobStages = z.infer<typeof resolvedJobStagesSchema>;

export class IntegrationsNotConfiguredError extends Error {
  constructor() {
    super('No ATS configured for this organization');
    this.name = 'IntegrationsNotConfiguredError';
  }
}
